import { StateMachine, Direction, flash, weightedChoice, fillArray, relativeDirection, WaitState } from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy, { EnemyState } from 'tower/scenes/battle/enemy';
import Projectile, { ProjectileKey } from './projectile';
import * as C from 'tower/constants';

interface Variant {
  idle: number;
  hp: number;
  projectile: ProjectileKey;
}

const VARIANTS: Record<string, Variant> = {
  red: {
    idle: 132,
    hp: 3,
    projectile: 'magic',
  },
  blue: {
    idle: 160,
    hp: 5,
    projectile: 'wallMagic',
  },
  dark: {
    idle: 164,
    hp: 4,
    projectile: 'magic',
  },
  darkWall: {
    idle: 164,
    hp: 4,
    projectile: 'wallMagic',
  },
};

export default class Wizard extends Enemy {
  constructor(
    scene: BattleScene,
    x: number,
    y: number,
    public variantType = 'blue',
  ) {
    const variant = VARIANTS[variantType];
    super(scene, x, y, 'enemies', 0, 'wizard', {
      hp: variant.hp,
      knockback: true,
      hideSpawn: true,
    });
    this.body.setSize(12, 12);
    this.setVisible(false);
    this.invulnerable = true;
  }

  makeStateMachine(scene: BattleScene): StateMachine<EnemyState | WaitState> {
    return new StateMachine('hiding', {
      hiding: new WaitState([100, 120], 'appear'),
      appear: new AppearWizardState(scene, this),
      attackWait: new WaitState(30, 'attack'),
      attack: new AttackWizardState(scene, this),
      postAttackWait: new WaitState(60, 'disappear'),
      disappear: new DisappearWizardState(scene, this),
    });
  }

  get direction() {
    return super.direction;
  }

  set direction(value: Direction) {
    super.direction = value;
    if (!this.variant) {
      return;
    }

    switch (value) {
      case Direction.Up:
        this.play(`wizard[${this.variantType}][up]`).setFlipX(false);
        return;
      case Direction.Down:
      case Direction.Right:
        this.play(`wizard[${this.variantType}][left]`).setFlipX(true);
        return;
      case Direction.Left:
        this.play(`wizard[${this.variantType}][left]`).setFlipX(false);
        return;
    }
  }

  get variant() {
    return VARIANTS[this.variantType];
  }

  static create(scene: BattleScene) {
    for (const [variantKey, variant] of Object.entries(VARIANTS)) {
      scene.anims.create({
        key: `wizard[${variantKey}][left]`,
        frameRate: 6,
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [variant.idle, variant.idle + 1],
        }),
        repeat: -1,
      });
      scene.anims.create({
        key: `wizard[${variantKey}][up]`,
        frameRate: 6,
        frames: scene.anims.generateFrameNumbers('enemies', {
          frames: [variant.idle + 2, variant.idle + 3],
        }),
        repeat: -1,
      });
    }
  }
}

class WizardState extends EnemyState {
  declare enemy: Wizard;
}

function spaceWeight(pos: number, playerPos: number) {
  const diff = Math.abs(playerPos - pos);
  if (diff < 1) {
    return 5;
  } else if (diff < 3) {
    return 2;
  }

  return 1;
}

class AppearWizardState extends WizardState {
  async handleEntered() {
    const { scene, enemy } = this;

    const [psx, psy] = scene.currentRoom.maze.worldXYToSpaceXY(scene.player.x, scene.player.y);

    const sx = weightedChoice(fillArray(C.MAZE_WIDTH - 1, (x) => [x, spaceWeight(x, psx)]));
    const sy = weightedChoice(
      fillArray(C.MAZE_HEIGHT - 1, (y) => [y, y === psy && sx === psx ? 0 : spaceWeight(y, psy)]),
    );

    enemy.setPosition(...scene.currentRoom.maze.spaceXYToWorldXY(sx, sy));
    enemy.direction = relativeDirection(enemy, scene.player);

    enemy.setVisible(true);
    await flash(scene, [enemy], 6, 500);
    enemy.invulnerable = false;
    return this.transition('attackWait');
  }
}

class AttackWizardState extends WizardState {
  async handleEntered() {
    const { scene, enemy } = this;
    new Projectile(scene, enemy.x, enemy.y, enemy.variant.projectile, enemy.direction, { target: 'player' });
    scene.sound.play('magic');
    return this.transition('postAttackWait');
  }
}

class DisappearWizardState extends WizardState {
  async handleEntered() {
    const { scene, enemy } = this;
    enemy.invulnerable = true;
    await flash(scene, [enemy], 6, 500);
    enemy.setVisible(false);
    return this.transition('hiding');
  }
}
